import { Link } from 'react-router-dom';
import { PublicRetirementCalculator, HeroSection } from '@/components/landing';
import { Footer } from '@/components/layout';

/**
 * Landing Page (Conversion-Optimized)
 * WP-LANDING-5: Redesigned with simplified hero and calculator-first layout
 *
 * Layout: Hero → Calculator → Problem/Solution/Trust → Final CTA
 *
 * Calculator is now immediately below hero with pre-filled values,
 * so visitors see their projection without scrolling or filling inputs.
 */
export function LandingPage() {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <Link to="/" className="text-2xl font-bold text-primary-600">
              FlowIO
            </Link>
            <nav className="flex gap-4">
              <Link
                to="/login"
                className="text-sm font-medium text-gray-600 hover:text-gray-900"
              >
                Sign In
              </Link>
              <Link
                to="/register"
                className="text-sm font-medium text-primary-600 hover:text-primary-500"
              >
                Get Started
              </Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main>
        {/* Section 1: Hero - A/B tested via PostHog (WP-LANDING-5) */}
        <HeroSection />

        {/* Section 2: Public Calculator (WP-LANDING-5) - Immediately after hero */}
        <section className="bg-white pt-8 pb-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <PublicRetirementCalculator />
          </div>
        </section>

        {/* Section 3: Problem Empathy */}
        <section className="bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-gray-900 text-center mb-4">
              Your finances are everywhere
            </h2>
            <p className="text-gray-600 text-center max-w-2xl mx-auto mb-12">
              You've got accounts scattered across different institutions. Getting
              the full picture means logging into half a dozen apps and doing math
              in a spreadsheet.
            </p>

            {/* Problem items grid */}
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {/* Banks */}
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center mb-4">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z" />
                  </svg>
                </div>
                <h3 className="font-medium text-gray-900 mb-2">Banks</h3>
                <p className="text-sm text-gray-600">
                  Checking, savings, maybe a CD or two. Each bank has its own app.
                </p>
              </div>

              {/* Brokerage */}
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center mb-4">
                  <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                </div>
                <h3 className="font-medium text-gray-900 mb-2">Brokerage</h3>
                <p className="text-sm text-gray-600">
                  Stocks, ETFs, mutual funds. Maybe multiple brokers over the years.
                </p>
              </div>

              {/* 401k/IRA */}
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center mb-4">
                  <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="font-medium text-gray-900 mb-2">401(k) / IRA</h3>
                <p className="text-sm text-gray-600">
                  Retirement accounts from current and former employers. Hard to track.
                </p>
              </div>

              {/* Real Estate */}
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <div className="w-10 h-10 rounded-lg bg-yellow-100 flex items-center justify-center mb-4">
                  <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                  </svg>
                </div>
                <h3 className="font-medium text-gray-900 mb-2">Real Estate</h3>
                <p className="text-sm text-gray-600">
                  Home equity, rental properties. Values change but who's tracking?
                </p>
              </div>

              {/* Loans */}
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center mb-4">
                  <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                  </svg>
                </div>
                <h3 className="font-medium text-gray-900 mb-2">Loans</h3>
                <p className="text-sm text-gray-600">
                  Mortgage, auto, student loans. These count against your net worth.
                </p>
              </div>

              {/* Spreadsheets */}
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center mb-4">
                  <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h3 className="font-medium text-gray-900 mb-2">Spreadsheets</h3>
                <p className="text-sm text-gray-600">
                  The manual way. Copy-paste, update, forget, repeat. There's a better way.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Section 4: Solution Overview */}
        <section className="bg-white">
          <div className="max-w-4xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-gray-900 text-center mb-4">
              One place for everything
            </h2>
            <p className="text-gray-600 text-center max-w-2xl mx-auto mb-12">
              FlowIO brings all your accounts together. Connect banks automatically,
              add manual accounts, or import statements. However you want to track it,
              we've got you covered.
            </p>

            {/* Solution items */}
            <div className="grid gap-8 sm:grid-cols-3">
              {/* Connect Banks */}
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                  </svg>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Connect Banks</h3>
                <p className="text-sm text-gray-600">
                  Securely link your accounts through Plaid. Balances and transactions
                  sync automatically.
                </p>
              </div>

              {/* Add Manually */}
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Add Manually</h3>
                <p className="text-sm text-gray-600">
                  Track real estate, private investments, or any account that can't
                  be linked automatically.
                </p>
              </div>

              {/* Import Statements */}
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                  </svg>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Import Statements</h3>
                <p className="text-sm text-gray-600">
                  Upload CSV or PDF statements. Our AI extracts and categorizes
                  transactions automatically.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Section 5: Trust Signals */}
        <section className="bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-gray-900 text-center mb-12">
              Your security is our priority
            </h2>

            {/* Trust items */}
            <div className="grid gap-6 sm:grid-cols-2">
              {/* Bank-Level Encryption */}
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                  <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">Bank-Level Encryption</h3>
                  <p className="text-sm text-gray-600">
                    256-bit AES encryption for all data at rest and in transit.
                    The same standard used by major banks.
                  </p>
                </div>
              </div>

              {/* Read-Only Access */}
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">Read-Only Access</h3>
                  <p className="text-sm text-gray-600">
                    We can only view your transactions. We can never move money
                    or make changes to your accounts.
                  </p>
                </div>
              </div>

              {/* Data Never Sold */}
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                  <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">Data Never Sold</h3>
                  <p className="text-sm text-gray-600">
                    Your financial data is yours. We never sell it to advertisers,
                    data brokers, or anyone else.
                  </p>
                </div>
              </div>

              {/* Enterprise Security */}
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                  <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">Enterprise Security</h3>
                  <p className="text-sm text-gray-600">
                    SOC 2 compliant infrastructure. Regular security audits.
                    Multi-factor authentication available.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Section 6: Final CTA */}
        <section className="bg-white">
          <div className="max-w-4xl mx-auto px-4 py-16 sm:px-6 lg:px-8 text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Know your number
            </h2>
            <p className="text-gray-600 max-w-xl mx-auto mb-8">
              Stop guessing. Stop juggling spreadsheets. See your complete
              financial picture in minutes.
            </p>
            <Link
              to="/register"
              className="inline-flex items-center justify-center px-8 py-4 text-lg font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg shadow-sm transition-colors"
            >
              Get Started Free
            </Link>
          </div>
        </section>
      </main>

      {/* Standard Footer (WP-LANDING-4) */}
      <Footer />
    </div>
  );
}

export default LandingPage;
